import { ColumnsType } from 'antd/es/table';
import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ServerTableActions, ServerTableRowActions } from 'shared/ui/ServerTable/types';
import { ReactComponent as FiltersIcon } from 'shared/assets/icons/FiltersIcon.svg';
import { ServerTable } from 'shared/ui/ServerTable';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { AppRoutes } from 'app/config/routerConfig/types';
import { getClientDateFormat } from 'shared/utils/helpers/getDateFormat';
import { ContractDetailsDrawer, contractDetailsDrawerActions } from 'widgets/ContractDetailsDrawer';
import { useAppDispatch } from 'app/config/storeConfig/hooks';
import {
  Invoice,
  generateInvoicePdfLink,
  useGetPaginatedInvoicesQuery,
  generateInvoicesPdfLink,
  calculateInvoiceStatus,
  calculateInvoicePercents,
  InvoiceStatus,
  InvoiceItemType,
} from 'entities/Invoice';
import classNames from 'classnames';
import { ExportEntity, ExportModal, getInvoiceColumnKeys, useOpenExportModal } from 'features/ExportModal';
import { useTableFilterContext } from 'features/TableFilter';
import { ReactComponent as ExportIcon } from 'shared/assets/icons/ExportIcon.svg';
import { useGetCurrencySymbol } from 'app/appState';
import { transformInvoicesListFilters } from '../utils/transformInvoicesListFilters';
import { InvoicesListFilter } from './InvoicesListFilter';
import { getUserName } from 'entities/User';
import dayjs from 'dayjs';
import { Progress } from 'antd';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { ReactComponent as DocumentIcon } from 'shared/assets/icons/DocumentIcon.svg';
import { InvoiceModal } from 'features/InvoiceModal';
import { ContractStatus } from '../model/types';
import { formatPrice } from 'shared/utils/helpers/formatPrice';

export const InvoicesTable: FC = memo(() => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const currencySymbol = useGetCurrencySymbol();

  const { openExportModal } = useOpenExportModal();

  const { t } = useAppTranslation(['contracts', 'common']);

  const { setFiltersOpened, appliedFilters, tags, clearAllFilters } = useTableFilterContext();

  const [selectedInvoice, setSelectedInvoice] = useState<Nullable<Invoice>>(null);

  const statusRefDate = useMemo(() => {
    const today = dayjs().startOf('day');

    return appliedFilters.statusRefDate?.value ? dayjs(appliedFilters.statusRefDate.value).startOf('day') : today;
  }, [appliedFilters.statusRefDate]);

  const goToUserContracts = useCallback(
    (userId: string): void => {
      navigate(`${AppRoutes.USERS}/${userId}/contracts`);
    },
    [navigate],
  );

  const openContractDetails = useCallback(
    (contractId: string): void => {
      dispatch(contractDetailsDrawerActions.setOpenContractDetails(contractId));
    },
    [dispatch],
  );

  const openInvoiceModal = useCallback((selectedInvoice: Invoice): void => {
    setSelectedInvoice(selectedInvoice);
  }, []);

  const closeInvoiceModal = useCallback((): void => {
    setSelectedInvoice(null);
  }, []);

  const rowActions: Array<ServerTableRowActions<Invoice>> = useMemo(
    () => [
      {
        name: 'downloadInvoice',
        theme: 'clear',
        description: t('Download', { ns: 'common' }),
        icon: <DocumentIcon className="[&>path]:stroke-primary" />,
        onClick: (selectedInvoice) => {
          const invoiceLink = generateInvoicePdfLink(selectedInvoice.invoiceId);

          window.open(invoiceLink);
        },
      },
    ],
    [t],
  );

  const columns = useMemo<ColumnsType<Invoice>>(
    () => [
      {
        title: t('Customer'),

        key: 'user',
        render: (_, record) => (
          <div
            className="text-accent font-semibold underline cursor-pointer"
            onClick={() => {
              goToUserContracts(record.user.userId);
            }}
          >
            {getUserName(record.user)}
          </div>
        ),
      },
      {
        title: t('Document'),
        key: 'documentNumber',

        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, record) => (
          <div
            className="text-accent underline cursor-pointer"
            onClick={() => {
              openInvoiceModal(record);
            }}
          >
            #{record.documentNumber} <br />
            {record.contract && !record.contract.isApplicable && `(${t('Not applicable')})`}
          </div>
        ),
      },
      {
        title: t('Unit type'),

        key: 'unitType',
        render: (_, record) => {
          const boxInvoiceItem = record.invoiceItems.find((invoiceItem) => invoiceItem.contractItem?.box);
          const box = boxInvoiceItem?.contractItem?.box;

          const depositInvoiceItem = record.invoiceItems.find((invoiceItem) => invoiceItem.invoiceItemType === InvoiceItemType.DEPOSIT);

          if (box) {
            return <div className="text-primary font-normal">{box.name}</div>;
          }

          if (!box && depositInvoiceItem) {
            return <div className="text-primary font-normal">{t(depositInvoiceItem.invoiceItemType)}</div>;
          }

          if (!record.contract?.contractId) {
            return <div className="text-primary font-normal">{t('Personal Invoice', { ns: 'common' })}</div>;
          }

          if (record.relatedInvoiceId) {
            return <div className="text-primary font-normal">{t('Late charge')}</div>;
          }

          return <div className="text-primary font-normal">{t('Other', { ns: 'common' })}</div>;
        },
      },

      {
        title: t('Contract number'),

        key: 'contract',
        render: (_, record) => {
          const leaveDate = record?.contract?.leaveDate ? dayjs(record.contract.leaveDate) : null;

          const isClosed = leaveDate ? leaveDate.isSame(statusRefDate, 'day') || leaveDate.isBefore(statusRefDate, 'day') : false;
          const status = isClosed ? ContractStatus.CLOSED : ContractStatus.ACTIVE;

          return (
            <div className="flex flex-col">
              <div
                className="text-accent underline cursor-pointer"
                onClick={() => {
                  if (record?.contract?.contractId) {
                    openContractDetails(record.contract.contractId);
                  }
                }}
              >
                #{record.contract?.contractNumber}
              </div>
              {record.contract && (
                <div className="pr-2">
                  {status === ContractStatus.CLOSED ? (
                    <div className="flex flex-col">
                      <span className="text-xs font-semibold text-error">{t('Closed')}</span>
                      <span className="text-xs">({getClientDateFormat(record.contract.leaveDate)})</span>
                    </div>
                  ) : (
                    <span className="text-xs font-semibold text-success">{t('Active')}</span>
                  )}
                </div>
              )}
            </div>
          );
        },
      },
      {
        title: t('Warehouse'),
        key: 'warehouse',

        render: (_, record) => <div className="text-primary font-normal">{record.warehouse?.name}</div>,
      },
      {
        title: t('Amount'),
        key: 'totalAmount',

        render: (_, record) => {
          return (
            <div className="text-primary font-normal">
              {formatPrice(record.amount)} {currencySymbol}
            </div>
          );
        },
      },
      {
        title: t('Status ({{statusRefDate}})', { statusRefDate: getClientDateFormat(statusRefDate) }),
        key: 'status',

        render: (_, record) => {
          const result = calculateInvoiceStatus(record, statusRefDate);
          const percents = calculateInvoicePercents(record.amount, result.paidAmount, result.creditedAmount);
          const debt = roundNumber(record.amount - result.paidAmount - result.creditedAmount);

          return (
            <div className="flex flex-col items-center">
              <div className="text-primary font-normal text-sm">
                {t(result.status)} {debt !== 0 ? `(${t('Debt')}: ${formatPrice(debt)} ${currencySymbol})` : ''}
              </div>
              <Progress
                size={['100%', 12]}
                percent={percents.credited + percents.paid}
                success={{ percent: percents.paid }}
                strokeColor="var(--warn)"
                trailColor={result.status === InvoiceStatus.OVERDUE ? 'var(--error)' : 'var(--primaryLight)'}
                showInfo={false}
              />
              <div className="w-full flex flex-row justify-around">
                {!!result.paidAmount && (
                  <span className="text-primary font-normal text-sm">
                    {t('Paid')}: {formatPrice(result.paidAmount)} {currencySymbol}
                  </span>
                )}
                {!!result.creditedAmount && (
                  <span className="text-primary font-normal text-sm">
                    {t('Credited')}: {formatPrice(result.creditedAmount)} {currencySymbol}
                  </span>
                )}
              </div>
            </div>
          );
        },
      },
      {
        title: t('Due date'),
        key: 'dueDate',

        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, record) => <div className="text-primary font-normal">{getClientDateFormat(record.dueDate)}</div>,
      },
      {
        title: t('Created date'),
        key: 'createdAt',

        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, record) => <div className="text-primary font-normal">{getClientDateFormat(record.createdAt)}</div>,
      },
    ],
    [t, statusRefDate, goToUserContracts, openContractDetails, currencySymbol],
  );

  const tableActions: Array<ServerTableActions<Invoice>> = useMemo(
    () => [
      {
        name: t('Export', { ns: 'common' }),
        theme: 'secondary',
        icon: <ExportIcon />,
        iconPosition: 'prev',
        onClick: () => {
          openExportModal({
            filters: transformInvoicesListFilters(appliedFilters),
            columns: getInvoiceColumnKeys(t),
            entity: ExportEntity.INVOICE,
          });
        },
      },
      {
        name: t('Download PDF', { ns: 'common' }),
        theme: 'secondary',
        onClick: () => {
          const invoiceLink = generateInvoicesPdfLink(transformInvoicesListFilters(appliedFilters));

          window.open(invoiceLink);
        },
      },
      {
        name: t('Filters', { ns: 'common' }),
        icon: <FiltersIcon />,
        theme: 'secondary',
        iconPosition: 'prev',
        onClick: () => {
          setFiltersOpened(true);
        },
      },
    ],
    [appliedFilters, openExportModal, setFiltersOpened, t],
  );

  // TO DO: add Invoice payment status column with color batces (as there are 7 Invoice statuses)
  const highlightRow = (record: Invoice): string => {
    return classNames({
      'bg-secondary': record.contract && !record.contract.isApplicable,
    });
  };

  return (
    <div className="pt-4">
      <div className="font-semibold text-3xl mb-7 font-tektur font-tektur">{t('Invoices')}</div>
      <ServerTable
        columns={columns}
        rowActions={rowActions}
        fetchData={useGetPaginatedInvoicesQuery}
        rowKey="invoiceId"
        search
        tableActionsPosition="prev"
        rowClassName={highlightRow}
        searchPlaceholder={t('Enter customer name or invoice | contract number')}
        tableActions={tableActions}
        tags={tags}
        onAllFiltersClear={clearAllFilters}
        defaultFilters={transformInvoicesListFilters(appliedFilters)}
      />
      <ContractDetailsDrawer />
      <ExportModal />
      <InvoicesListFilter />
      <InvoiceModal onClose={closeInvoiceModal} invoice={selectedInvoice} />
    </div>
  );
});
